import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import PageHeader from "../../components/PageHeader";
import SettingsCreateWizardLayout from "../settings/SettingsCreateWizardLayout";
import SettingsWizardFooter from "../settings/SettingsWizardFooter";
import { formattedError } from "../../components/formatted/FormattedText";
import { DEFAULT_USER_CREATE_FORM } from "./userAccessDefaults";
import {
  getWizardStepIndex,
  normalizeUserFormForDirtyCheck,
  validateAllUserWizardSteps,
  validateUserWizardStep,
} from "./userAccessCreateWizard";
import {
  UserAccessGeneralSection,
  UserAccessNotificationsSection,
  UserAccessReportsSection,
  UserAccessSitesSection,
} from "./UserAccessFormSections";

const UserAccessCreateEditor = ({ corporation, viewMode }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const API = process.env.REACT_APP_API_BACKEND;
  const token = Cookies.get("userToken");

  const googleSSORequired = !!(corporation && corporation.googleSSORequired);
  const corporationId = corporation?._id;
  const maxUserCount = corporation?.maxUserCount;

  const [formData, setFormData] = useState(DEFAULT_USER_CREATE_FORM);
  const [activeStep, setActiveStep] = useState("general");
  const [maxReachedStepIndex, setMaxReachedStepIndex] = useState(0);
  const [errorForm, setErrorForm] = useState("");
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const multiselectAddressRef = useRef();

  const steps = useMemo(
    () => [
      { key: "general", label: t("settingsUsers.stepProfile") },
      { key: "sites", label: t("settingsUsers.stepSites") },
      { key: "notifications", label: t("settingsUsers.stepNotifications") },
      { key: "reports", label: t("settingsUsers.stepReports") },
    ],
    [t],
  );

  const currentStepIndex = getWizardStepIndex(activeStep);
  const isLastStep = currentStepIndex === steps.length - 1;

  const hasUnsavedChanges = useMemo(() => {
    const initial = JSON.stringify(
      normalizeUserFormForDirtyCheck(DEFAULT_USER_CREATE_FORM),
    );
    const current = JSON.stringify(normalizeUserFormForDirtyCheck(formData));
    return current !== initial;
  }, [formData]);

  useEffect(() => {
    if (!token) return;

    const fetchSites = async () => {
      try {
        const res = await axios.get(`${API}address/getAddressNameList`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (res.status === 200) {
          setSites(res.data.addressNameList || []);
        }
      } catch (err) {
        console.error(err);
        setSites([]);
      }
    };

    fetchSites();
  }, [API, token]);

  useEffect(() => {
    if (!corporationId || viewMode === "admin") return;

    const checkLicense = async () => {
      try {
        const response = await axios.get(`${API}users?page=1&limit=500`, {
          headers: { authorization: `Bearer ${token}` },
        });
        const users = response.data?.data || [];
        const filteredCount = users.filter(
          (user) =>
            !user.email.endsWith("@akanthas.com") &&
            !user.email.endsWith("@demo.com"),
        ).length;
        if (filteredCount >= maxUserCount) {
          toast.error(t("settingsUsers.userLimitReached"), {
            className: "custom-toast",
          });
          history.replace("/access");
        }
      } catch {
        /* ignore */
      }
    };

    checkLicense();
  }, [API, corporationId, history, maxUserCount, t, token, viewMode]);

  const handleAuthorizedAddresses = (addressesToAdd) => {
    setSelectedSites(addressesToAdd);
    setFormData((prev) => ({
      ...(prev || {}),
      authorizedAddresses: addressesToAdd.map((a) => a._id),
    }));
  };

  const goToStep = (stepKey) => {
    setActiveStep(stepKey);
  };

  const goToCreateStep = (_index, step) => {
    goToStep(step.key);
  };

  const showValidationError = (message) => {
    toast.error(message, { className: "custom-toast" });
  };

  const goToNextStep = () => {
    const result = validateUserWizardStep(activeStep, formData, t, {
      googleSSORequired,
    });
    if (!result.valid) {
      showValidationError(result.message);
      return;
    }
    const idx = currentStepIndex;
    if (idx < steps.length - 1) {
      goToStep(steps[idx + 1].key);
      setMaxReachedStepIndex((prev) => Math.max(prev, idx + 1));
    }
  };

  const createUser = async () => {
    const validation = validateAllUserWizardSteps(formData, t, {
      googleSSORequired,
    });
    if (!validation.valid) {
      showValidationError(validation.message);
      return false;
    }

    setSaving(true);
    setErrorForm("");
    try {
      const response = await axios.post(`${API}user/add`, formData, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (response?.status === 200) {
        toast.success(t("The user has been successfully created"), {
          className: "custom-toast",
        });
        return true;
      }
      toast.error(t("An error occurred"), { className: "custom-toast" });
      return false;
    } catch (error) {
      const message = formattedError(error.response?.data?.error);
      setErrorForm(message);
      toast.error(message, { className: "custom-toast" });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const stepContent = (() => {
    switch (activeStep) {
      case "general":
        return (
          <UserAccessGeneralSection
            data={formData}
            setData={setFormData}
            isCreate={true}
            googleSSORequired={googleSSORequired}
            setErrorForm={setErrorForm}
            passwordResetKey="create-wizard"
          />
        );
      case "sites":
        return (
          <UserAccessSitesSection
            sites={sites}
            selectedSites={selectedSites}
            onAuthorizedAddressesChange={handleAuthorizedAddresses}
            multiselectAddressRef={multiselectAddressRef}
          />
        );
      case "notifications":
        return (
          <UserAccessNotificationsSection
            data={formData}
            setData={setFormData}
          />
        );
      case "reports":
        return (
          <UserAccessReportsSection data={formData} setData={setFormData} />
        );
      default:
        return null;
    }
  })();

  if (!token) return null;

  return (
    <div className="settings-page-wrapper settings-page-wrapper--wizard">
      <PageHeader
        title={t("settingsUsers.createNewUser")}
        showBackButton={true}
        backRoute="/access"
        backLabel={t("settingsUsers.quit")}
        tabs={[]}
      />

      <SettingsCreateWizardLayout
        steps={steps}
        currentIndex={currentStepIndex}
        maxReachedIndex={maxReachedStepIndex}
        onStepClick={goToCreateStep}
        ariaLabel={t("settingsUsers.wizardAriaLabel")}
        stepperClassName="ui-stepper--labels-wrap"
        contentClassName="settings-create-wizard--user"
        panelClassName="settings-create-wizard__panel--user-general"
      >
        <div className="w-full max-w-[640px] flex flex-col">
          {errorForm ? (
            <section className="user-access-form-error mb-4">
              <p>{errorForm}</p>
            </section>
          ) : null}
          {stepContent}
        </div>
      </SettingsCreateWizardLayout>

      <SettingsWizardFooter
        stepIndex={currentStepIndex}
        saving={saving}
        onBack={() => goToStep(steps[currentStepIndex - 1].key)}
        onQuit={async () => {
          if (!hasUnsavedChanges || currentStepIndex === 0) {
            history.push("/access");
            return;
          }
          const created = await createUser();
          if (created) {
            history.push("/access");
          }
        }}
        quitLabel={
          hasUnsavedChanges && isLastStep
            ? t("settingsUsers.quitAndSave")
            : t("settingsUsers.quit")
        }
        primaryLabel={
          isLastStep ? t("settingsUsers.createTheUser") : t("Continue")
        }
        onPrimary={
          isLastStep
            ? async () => {
                const created = await createUser();
                if (created) {
                  history.push("/access");
                }
              }
            : goToNextStep
        }
        primaryDisabled={saving}
      />
    </div>
  );
};

export default UserAccessCreateEditor;
